// src/pages/ProductionBatches.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Factory,
  Plus,
  Search,
  Calendar,
  Layers,
  TrendingUp,
  DollarSign,
  Package,
  History,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Filter,
  Boxes,
} from 'lucide-react';
import { ProductionBatch, StockMovement, Product } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { formatDateTime } from '../lib/date';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Modal } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { EmptyState } from '../components/ui/EmptyState';

export const ProductionBatches: React.FC = () => {
  const { user, can } = useAuth();
  const { success, error: toastError } = useToast();

  const [activeTab, setActiveTab] = useState<'batches' | 'ledger'>('batches');
  const [batches, setBatches] = useState<ProductionBatch[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [productsList, setProductsList] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Log Production Batch Modal
  const [batchModalOpen, setBatchModalOpen] = useState(false);
  const [submittingBatch, setSubmittingBatch] = useState(false);
  const [batchForm, setBatchForm] = useState({
    batch_date: new Date().toISOString().substring(0, 10),
    product_id: '',
    qty: '',
    unit_cost: '',
    overhead_pct: '23',
    notes: '',
    auto_update_product_cost: true,
  });

  // Manual Stock Movement Modal
  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [submittingStock, setSubmittingStock] = useState(false);
  const [stockForm, setStockForm] = useState({
    movement_date: new Date().toISOString().substring(0, 10),
    movement_type: 'Purchase',
    product_id: '',
    qty: '',
    unit_price: '',
    reference_no: '',
    notes: '',
  });

  // Load products list for dropdowns
  const loadProducts = useCallback(async () => {
    try {
      const res = await apiRequest<any>('/api/products?pageSize=100');
      // /api/products returns { items: Product[], pagination: ... } wrapped in ApiResponse
      const list = res?.items || res?.data?.items || res?.data || [];
      setProductsList(Array.isArray(list) ? list : []);
    } catch (err: any) {
      console.error('Failed to load products list:', err);
    }
  }, []);

  const loadBatches = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        search,
      });
      const res = await apiRequest<any>(`/api/production/batches?${query}`);
      const batchList = res?.data || res?.items || [];
      setBatches(Array.isArray(batchList) ? batchList : []);
      setTotalPages(res?.pagination?.totalPages || 1);
      setTotalCount(res?.pagination?.total || 0);
    } catch (err: any) {
      toastError(err.message || 'Failed to load production batches.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, toastError]);

  const loadMovements = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
      });
      const res = await apiRequest<any>(`/api/inventory/movements?${query}`);
      const moveList = res?.data || res?.items || [];
      setMovements(Array.isArray(moveList) ? moveList : []);
      setTotalPages(res?.pagination?.totalPages || 1);
      setTotalCount(res?.pagination?.total || 0);
    } catch (err: any) {
      toastError(err.message || 'Failed to load stock movements.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, toastError]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    if (activeTab === 'batches') {
      loadBatches();
    } else {
      loadMovements();
    }
  }, [activeTab, loadBatches, loadMovements]);

  // Selected product helper for batch modal
  const selectedProduct = productsList.find(p => p.id === Number(batchForm.product_id));

  const handleProductSelectChange = (pidStr: string) => {
    const prod = productsList.find(p => p.id === Number(pidStr));
    setBatchForm(prev => ({
      ...prev,
      product_id: pidStr,
      overhead_pct: prod?.overhead_pct ? prod.overhead_pct.toString() : prod?.category === 'Manufacturing' ? '23' : '5',
      unit_cost: prod?.cost ? prod.cost.toString() : prev.unit_cost,
    }));
  };

  const handleCreateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!batchForm.product_id || !batchForm.qty || !batchForm.unit_cost) {
      toastError('Please fill in product, quantity, and unit cost.');
      return;
    }

    setSubmittingBatch(true);
    try {
      const res = await apiRequest<{ message: string }>('/api/production/batches', {
        method: 'POST',
        body: JSON.stringify({
          batch_date: batchForm.batch_date,
          product_id: Number(batchForm.product_id),
          qty: Number(batchForm.qty),
          unit_cost: Number(batchForm.unit_cost),
          overhead_pct: Number(batchForm.overhead_pct || '23'),
          notes: batchForm.notes,
          auto_update_product_cost: batchForm.auto_update_product_cost,
        }),
      });

      success(res.message || 'Batch recorded successfully.');
      setBatchModalOpen(false);
      setBatchForm({
        batch_date: new Date().toISOString().substring(0, 10),
        product_id: '',
        qty: '',
        unit_cost: '',
        overhead_pct: '23',
        notes: '',
        auto_update_product_cost: true,
      });
      loadBatches();
    } catch (err: any) {
      toastError(err.message || 'Failed to log production batch.');
    } finally {
      setSubmittingBatch(false);
    }
  };

  const handleCreateStock = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stockForm.product_id || !stockForm.qty) {
      toastError('Please select product and quantity.');
      return;
    }

    setSubmittingStock(true);
    try {
      const res = await apiRequest<{ message: string }>('/api/inventory/movements', {
        method: 'POST',
        body: JSON.stringify({
          movement_date: stockForm.movement_date,
          movement_type: stockForm.movement_type,
          product_id: Number(stockForm.product_id),
          qty: Number(stockForm.qty),
          unit_price: stockForm.unit_price ? Number(stockForm.unit_price) : undefined,
          reference_no: stockForm.reference_no,
          notes: stockForm.notes,
        }),
      });

      success(res.message || 'Stock movement recorded.');
      setStockModalOpen(false);
      setStockForm({
        movement_date: new Date().toISOString().substring(0, 10),
        movement_type: 'Purchase',
        product_id: '',
        qty: '',
        unit_price: '',
        reference_no: '',
        notes: '',
      });
      loadMovements();
    } catch (err: any) {
      toastError(err.message || 'Failed to record stock movement.');
    } finally {
      setSubmittingStock(false);
    }
  };

  // Preview batch totals
  const numQty = parseFloat(batchForm.qty) || 0;
  const numUnitCost = parseFloat(batchForm.unit_cost) || 0;
  const numOverhead = parseFloat(batchForm.overhead_pct) || 0;
  const estTotalCost = numQty * numUnitCost;
  const estMarkupPrice = numUnitCost * (1 + numOverhead / 100);

  return (
    <div className="space-y-6">
      {/* Title & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-bold text-slate-900">Production Batches & Stock Ledger</h2>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 flex items-center gap-1">
              <Factory className="w-3 h-3 text-emerald-600" />
              Real-Time Inventory & Costing
            </span>
          </div>
          <p className="text-xs text-slate-500">
            Log daily manufacturing output, track stock movements, and synchronize deductions from sales invoices
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setStockModalOpen(true)}
            icon={<Boxes className="w-4 h-4 text-slate-600" />}
          >
            Record Stock In/Out
          </Button>

          <Button
            variant="primary"
            onClick={() => setBatchModalOpen(true)}
            icon={<Plus className="w-4 h-4" />}
          >
            Log Production Batch
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => { setActiveTab('batches'); setPage(1); }}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'batches'
              ? 'border-[#0f5a2e] text-[#0f5a2e]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Factory className="w-4 h-4" />
          <span>Production Batches ({batches.length})</span>
        </button>

        <button
          onClick={() => { setActiveTab('ledger'); setPage(1); }}
          className={`px-4 py-2.5 text-sm font-semibold border-b-2 flex items-center gap-2 transition-colors ${
            activeTab === 'ledger'
              ? 'border-[#0f5a2e] text-[#0f5a2e]'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>Stock Movement Ledger</span>
        </button>
      </div>

      {/* Batches Tab View */}
      {activeTab === 'batches' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <TableSkeleton rows={8} columns={7} />
            ) : batches.length === 0 ? (
              <EmptyState
                icon={<Factory className="w-10 h-10 text-slate-400" />}
                title="No production batches found"
                description="Click 'Log Production Batch' above to record factory production runs."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Product / Material</th>
                      <th className="px-3 py-3 text-center">Unit</th>
                      <th className="px-4 py-3 text-right">Batch Qty</th>
                      <th className="px-4 py-3 text-right bg-amber-50/40 text-amber-950 font-bold">Unit Cost (AED)</th>
                      <th className="px-4 py-3 text-right">Total Batch Cost (AED)</th>
                      <th className="px-4 py-3 text-right bg-emerald-50/40 text-emerald-950 font-bold">Net Price (+{batches[0]?.overhead_pct || 23}% AED)</th>
                      <th className="px-4 py-3 text-left">Logged By</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {batches.map(b => (
                      <tr key={b.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-700">
                          {b.batch_date}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-semibold text-slate-900 text-sm">{b.product_name}</span>
                          {b.notes && (
                            <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-1">{b.notes}</p>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-md font-mono">
                            {b.unit || 'Unit'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs font-semibold text-slate-900">
                          {b.qty.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs font-bold text-amber-900 bg-amber-50/20 whitespace-nowrap">
                          AED {b.unit_cost.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs font-medium text-slate-800 whitespace-nowrap">
                          AED {b.total_cost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-xs font-bold text-emerald-900 bg-emerald-50/20 whitespace-nowrap">
                          AED {(b.markup_price || b.unit_cost * 1.23).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-500">
                          {b.created_by_name || 'System Admin'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stock Movement Ledger View */}
      {activeTab === 'ledger' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            {loading ? (
              <TableSkeleton rows={8} columns={7} />
            ) : movements.length === 0 ? (
              <EmptyState
                icon={<Boxes className="w-10 h-10 text-slate-400" />}
                title="No stock movements recorded"
                description="Stock movements recorded from production runs, purchases, or adjustments will appear here."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-slate-600">
                  <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-3 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-left">Product</th>
                      <th className="px-3 py-3 text-center">Unit</th>
                      <th className="px-4 py-3 text-right">Movement Qty</th>
                      <th className="px-4 py-3 text-right font-bold text-slate-900">Stock Balance</th>
                      <th className="px-4 py-3 text-left">Ref / Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {movements.map(m => {
                      const isInflow = ['Opening Stock', 'Purchase', 'Production', 'Return', 'Excess'].includes(m.movement_type);
                      return (
                        <tr key={m.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-700">
                            {m.movement_date}
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span
                              className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                                isInflow
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : 'bg-rose-50 text-rose-700 border border-rose-200'
                              }`}
                            >
                              {m.movement_type}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-slate-900 text-sm">
                            {m.product_name}
                          </td>
                          <td className="px-3 py-3 text-center whitespace-nowrap font-mono text-xs text-slate-500">
                            {m.unit || 'Unit'}
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs font-bold">
                            <span className={isInflow ? 'text-emerald-700' : 'text-rose-700'}>
                              {isInflow ? '+' : '-'}{m.qty.toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right font-mono text-xs font-extrabold text-slate-900 bg-slate-50/50">
                            {m.balance_after !== undefined ? m.balance_after.toLocaleString() : '—'}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {m.reference_no && <span className="font-mono text-slate-600 font-semibold mr-1">[{m.reference_no}]</span>}
                            {m.notes || '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Log Production Batch Modal */}
      <Modal
        isOpen={batchModalOpen}
        onClose={() => setBatchModalOpen(false)}
        title="Record Production Batch"
      >
        <form onSubmit={handleCreateBatch} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Batch Date *</label>
              <Input
                type="date"
                required
                value={batchForm.batch_date}
                onChange={e => setBatchForm(prev => ({ ...prev, batch_date: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Overhead Top-Up %</label>
              <Input
                type="number"
                step="0.1"
                required
                value={batchForm.overhead_pct}
                onChange={e => setBatchForm(prev => ({ ...prev, overhead_pct: e.target.value }))}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Product / Finished Good *</label>
            <select
              required
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#0f5a2e] focus:border-transparent bg-white"
              value={batchForm.product_id}
              onChange={e => handleProductSelectChange(e.target.value)}
            >
              <option value="">-- Select Product --</option>
              {productsList.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.category}) - Current Stock: {p.stock_balance || 0} {p.unit || ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Batch Output Qty ({selectedProduct?.unit || 'units'}) *
              </label>
              <Input
                type="number"
                step="any"
                min="0.01"
                required
                placeholder="e.g. 100 or 300"
                value={batchForm.qty}
                onChange={e => setBatchForm(prev => ({ ...prev, qty: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Factory Unit Cost (AED) *
              </label>
              <Input
                type="number"
                step="any"
                min="0"
                required
                placeholder="e.g. 40.00"
                value={batchForm.unit_cost}
                onChange={e => setBatchForm(prev => ({ ...prev, unit_cost: e.target.value }))}
              />
            </div>
          </div>

          {/* Live Calculation Preview */}
          {numQty > 0 && numUnitCost > 0 && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl space-y-1.5 text-xs">
              <div className="flex justify-between text-slate-700">
                <span>Total Batch Production Cost:</span>
                <strong className="font-mono text-slate-900">AED {estTotalCost.toFixed(2)}</strong>
              </div>
              <div className="flex justify-between text-emerald-800">
                <span>Derived Net Cost (Markup +{numOverhead}%):</span>
                <strong className="font-mono text-emerald-950">AED {estMarkupPrice.toFixed(2)}</strong>
              </div>
              <div className="flex justify-between text-slate-600 pt-1 border-t border-emerald-200">
                <span>Stock Balance After Batch:</span>
                <span className="font-mono font-bold text-slate-900">
                  {((selectedProduct?.stock_balance || 0) + numQty).toLocaleString()} {selectedProduct?.unit || 'units'}
                </span>
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Production Notes / Batch Code</label>
            <Input
              type="text"
              placeholder="e.g. Mixing run #104, Shift B"
              value={batchForm.notes}
              onChange={e => setBatchForm(prev => ({ ...prev, notes: e.target.value }))}
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="auto_update_cost"
              checked={batchForm.auto_update_product_cost}
              onChange={e => setBatchForm(prev => ({ ...prev, auto_update_product_cost: e.target.checked }))}
              className="rounded text-[#0f5a2e] focus:ring-[#0f5a2e] h-4 w-4"
            />
            <label htmlFor="auto_update_cost" className="text-xs text-slate-700 cursor-pointer font-medium">
              Automatically update catalog product base cost and net cost to this batch cost
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setBatchModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={submittingBatch}>
              Save Production Batch
            </Button>
          </div>
        </form>
      </Modal>

      {/* Manual Stock In/Out Modal */}
      <Modal
        isOpen={stockModalOpen}
        onClose={() => setStockModalOpen(false)}
        title="Record Stock Movement (Transaction Ledger)"
      >
        <form onSubmit={handleCreateStock} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Movement Date *</label>
              <Input
                type="date"
                required
                value={stockForm.movement_date}
                onChange={e => setStockForm(prev => ({ ...prev, movement_date: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Movement Type *</label>
              <select
                required
                className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#0f5a2e] bg-white"
                value={stockForm.movement_type}
                onChange={e => setStockForm(prev => ({ ...prev, movement_type: e.target.value }))}
              >
                <optgroup label="Stock Inflow (+)">
                  <option value="Purchase">Purchase (Trading Inflow)</option>
                  <option value="Opening Stock">Opening Stock</option>
                  <option value="Return">Sales Return</option>
                  <option value="Excess">Stock Excess / Adjustment</option>
                </optgroup>
                <optgroup label="Stock Outflow (-)">
                  <option value="Sales">Direct Sales Outflow</option>
                  <option value="Lab Use">Lab Testing / QC Sample</option>
                  <option value="Damage">Damage / Scrap</option>
                  <option value="Wastage">Factory Wastage</option>
                  <option value="Sample">Marketing Sample</option>
                  <option value="Less">Inventory Loss</option>
                </optgroup>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Product *</label>
            <select
              required
              className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#0f5a2e] bg-white"
              value={stockForm.product_id}
              onChange={e => setStockForm(prev => ({ ...prev, product_id: e.target.value }))}
            >
              <option value="">-- Select Product --</option>
              {productsList.map(p => (
                <option key={p.id} value={p.id}>
                  {p.name} - Available Stock: {p.stock_balance || 0} {p.unit || ''}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Quantity *</label>
              <Input
                type="number"
                step="any"
                min="0.01"
                required
                placeholder="Qty"
                value={stockForm.qty}
                onChange={e => setStockForm(prev => ({ ...prev, qty: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Unit Price (AED)</label>
              <Input
                type="number"
                step="any"
                placeholder="Unit purchase cost"
                value={stockForm.unit_price}
                onChange={e => setStockForm(prev => ({ ...prev, unit_price: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Reference No / PO</label>
              <Input
                type="text"
                placeholder="e.g. PO-8821 or ADJ-004"
                value={stockForm.reference_no}
                onChange={e => setStockForm(prev => ({ ...prev, reference_no: e.target.value }))}
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Notes</label>
              <Input
                type="text"
                placeholder="Reason or supplier name"
                value={stockForm.notes}
                onChange={e => setStockForm(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setStockModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={submittingStock}>
              Record Stock Movement
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
